<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useAuthStore } from "~/plugins/store/auth";
import { translation, useTranslationStore } from "~/plugins/store/translations";
import type { TranslationList } from "~/types/Utils";
import { useNuxtApp } from "#app";
import { useUserStore } from "~/plugins/store/user";
import { useReCaptcha } from "vue-recaptcha-v3";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import AccountLayout from "~/layouts/AccountLayout.vue";
import { useVehicleDiaryStore } from "~/plugins/store/vehicle-diary";
import {
  createDefaultValidationVehicleDiary,
  createDefaultVehicleDiary,
  type ValidationVehicleDiary,
  type VehicleDiary
} from "~/types/VehicleDiary";
import { recaptcha } from "~/composables/recaptcha";
import { type ApiVehicleDetails, createDefaultApiVehicleDetails } from "~/types/Vehicle";
import { useVehicleStore } from "~/plugins/store/vehicle";
import VehicleText from "~/components/rca/VehicleText.vue";
import VehicleEventAmount from "~/components/vehicle/VehicleEventAmount.vue";

export default defineComponent({
  components: { VehicleEventAmount, VehicleText, AccountLayout, LoadingDialog },
  setup() {
    definePageMeta({
      middleware: ['auth'],
    });

    useHead({
      title: 'Xiga | Jurnal de mașină',
      meta: [
        {
          name: 'description',
          content: 'META'
        }
      ],
    });

    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const { $toast } = useNuxtApp();
    const userStore = useUserStore();
    const recaptchaInstance = useReCaptcha();
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const route = useRoute();
    const vehicleId = parseInt(route.params.id as string, 10);
    const vehicleDiaryStore = useVehicleDiaryStore();
    const vehicleStore = useVehicleStore();
    const vehicle = ref(createDefaultApiVehicleDetails() as ApiVehicleDetails | null);
    const diaryForm = ref<VehicleDiary>(createDefaultVehicleDiary());
    const validationVehicleDiary = ref<ValidationVehicleDiary>(createDefaultValidationVehicleDiary());

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      if (accessToken) {
        showLoadingDialog.value = true;
        messageLoadingDialog.value = 'Se încarcă datele...';
        vehicle.value = await vehicleStore.getVehicle(vehicleId, accessToken);
        if (vehicle.value) {
          diaryForm.value.vehicle_id = vehicle.value.id;
        }
        showLoadingDialog.value = false;
      }
    });

    const handleValidationVehicleDiaryStatus = ({ field, isValid }: { field: string, isValid: boolean }) => {
      validationVehicleDiary.value[field] = isValid;
    };

    const getManufacturerLogo = (manufacturer: string) => {
      const imagePath = '/images/cars/' + manufacturer + '.png';
      return imageExists(imagePath) ? imagePath : '/images/cars/no-make.png';
    };

    const imageExists = (url: string) => {
      try {
        const http = new XMLHttpRequest();
        http.open('HEAD', url, false);
        http.send();
        return http.status !== 404;
      } catch (error) {
        return false;
      }
    };

    const getExpirationClass = (expirationDate: string | null | undefined) => {
      if (!expirationDate) return 'text-red';
      const now = new Date();
      const expiration = new Date(expirationDate);
      const diffTime = expiration.getTime() - now.getTime();
      const diffDays = diffTime / (1000 * 3600 * 24);

      if (diffDays < 0) {
        return 'text-red';
      } else if (diffDays < 60) {
        return 'text-warning';
      } else {
        return 'text-success';
      }
    };

    const createVehicleDiary = async () => {
      let isFormValid = true;

      // Check validation status for each field
      for (const [field, isValid] of Object.entries(validationVehicleDiary.value)) {
        if (!isValid) {
          console.log(`Validation failed for field: ${field}`);
          window.dispatchEvent(new CustomEvent('validate-vehicle', { detail: field }));
          isFormValid = false;
        }
      }

      // If the form is not valid, do not proceed
      if (!isFormValid) {
        $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
        return;
      }

      // Proceed with the creation if the form is valid
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.creating_diary, 'Creăm jurnalul...');

      try {
        const token = <string>await recaptcha(recaptchaInstance);
        const response = await vehicleDiaryStore.createDiary(diaryForm.value, <string>accessToken, token);
        if (response) {
          $toast.success(translation(translationList.value.diary_created, 'Jurnalul a fost creat cu succes!'));
          navigateTo('/account/vehicles/' + vehicleId + '/diaries/');
        } else {
          $toast.error(translation(translationList.value.error, 'A apărut o eroare.'));
        }
      } catch (error) {
        console.error('Error creating diary:', error);
        $toast.error(translation(translationList.value.error, 'A apărut o eroare.'));
      } finally {
        showLoadingDialog.value = false;
      }
    };

    return {
      user,
      accessToken,
      translationList,
      translation,
      showLoadingDialog,
      messageLoadingDialog,
      getManufacturerLogo,
      getExpirationClass,
      vehicle,
      handleValidationVehicleDiaryStatus,
      diaryForm,
      createVehicleDiary
    };
  }
});
</script>

<template>
  <account-layout class="profile">
    <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
    <v-container class="content-container" v-if="vehicle">
      <v-container fluid class="component-container-1400mw">
        <v-container class="background-ghost-white" style="border-radius: 15px;">
          <v-row class="mt-3">
            <v-col cols="12" sm="auto" md="12" class="d-flex flex-column justify-start centered-on-mobile">
              <h3 class="text-primary">
                <span class="cursor-pointer" @click="navigateTo('/account/vehicles/' + vehicle.id + '/')">
                  {{ vehicle.rca_vehicle_make_name }} {{ vehicle.rca_vehicle_model_name }}
                </span>
                <v-icon>mdi-chevron-right</v-icon>
                <span class="cursor-pointer" @click="navigateTo('/account/vehicles/' + vehicle.id + '/diaries/')">
                  {{ translation(translationList.vehicle_diaries, 'Jurnale de mașină') }}
                </span>
                <v-icon>mdi-chevron-right</v-icon>
                {{ translation(translationList.vehicle_diaries, 'Adaugă jurnal') }}
              </h3>
              <p class="font-weight-regular">
                <span class="font-weight-bold">{{ vehicle.registration_plate }} {{ vehicle.vin }}</span>.
              </p>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-card>
                <v-card-title>
                  <h4 class="text-primary">
                    {{ translation(translationList.vehicle_diaries, 'Adaugă jurnal') }}
                  </h4>
                </v-card-title>
                <v-card-text>
                  <v-row>
                    <v-col cols="12" sm="6">
                      <vehicle-event-date
                          v-model="diaryForm.event_date"
                          @validation-vehicle-status="handleValidationVehicleDiaryStatus"
                      />
                    </v-col>
                    <v-col cols="12" sm="6">
                      <vehicle-number-of-kilometers
                          v-model="diaryForm.number_of_kilometers"
                          @validation-vehicle-status="handleValidationVehicleDiaryStatus"/>
                    </v-col>
                    <v-col cols="12">
                      <vehicle-diary-event-type-select
                          v-model="diaryForm.event_type"
                          @validation-vehicle-status="handleValidationVehicleDiaryStatus"/>
                    </v-col>
                    <v-col cols="6">
                      <vehicle-event-amount
                          v-model="diaryForm.event_amount"
                          @validation-vehicle-status="handleValidationVehicleDiaryStatus"/>
                    </v-col>
                    <v-col cols="6">
                      <vehicle-diary-event-amount-currency-select
                          v-model="diaryForm.event_amount_currency"
                          @validation-vehicle-status="handleValidationVehicleDiaryStatus"/>
                    </v-col>
                    <v-col cols="12">
                      <vehicle-text
                          v-model="diaryForm.text"
                          @validation-vehicle-status="handleValidationVehicleDiaryStatus"/>
                    </v-col>
                  </v-row>
                  <v-row class="mt-10 mb-15">
                    <v-col cols="12" class="d-flex align-center justify-center">
                      <v-btn
                          class="block-on-mobile"
                          color="primary"
                          size="x-large"
                          flat
                          rounded="lg"
                          @click="createVehicleDiary"
                      >
                        {{ translation(translationList.view, 'Salvează') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </v-container>
    </v-container>
  </account-layout>
</template>

<style scoped>

</style>
